import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, DollarSign, TrendingUp, AlertCircle, MessageSquare, LogOut, Shield, 
  Activity, CreditCard, RefreshCcw, Ban, CheckCircle, Clock, XCircle,
  BarChart3, PieChart, Trash2, Database, Mail, Key, AlertTriangle, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [operators, setOperators] = useState([]);
  const [supportRequests, setSupportRequests] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [paymentFailures, setPaymentFailures] = useState([]);
  const [dbStats, setDbStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('operators');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [dashboardRes, operatorsRes, supportRes, activityRes, revenueRes, failuresRes, dbStatsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers }),
        axios.get(`${API}/admin/operators`, { headers }),
        axios.get(`${API}/admin/support-requests`, { headers }),
        axios.get(`${API}/admin/activity-logs`, { headers }),
        axios.get(`${API}/admin/revenue`, { headers }),
        axios.get(`${API}/admin/payment-failures`, { headers }),
        axios.get(`${API}/admin/database/stats`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setStats(dashboardRes.data);
      setOperators(operatorsRes.data);
      setSupportRequests(supportRes.data);
      setActivityLogs(activityRes.data);
      setRevenue(revenueRes.data);
      setPaymentFailures(failuresRes.data);
      setDbStats(dbStatsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/app/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async (operatorId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API}/admin/operators/${operatorId}/suspend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Operator ${operatorId} suspended`);
      fetchData();
    } catch (error) {
      toast.error('Failed to suspend operator');
    }
  };

  const handleReactivate = async (operatorId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API}/admin/operators/${operatorId}/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Operator ${operatorId} reactivated`);
      fetchData();
    } catch (error) {
      toast.error('Failed to reactivate operator');
    }
  };

  const handleDeleteOperator = async (operatorId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${API}/admin/operators/${operatorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Operator ${operatorId} deleted`);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete operator');
    }
  };

  const handleVerifyEmail = async (operatorId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API}/admin/operators/${operatorId}/verify-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Email verified for ${operatorId}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to verify email');
    }
  };

  const handleCleanupTestUsers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.delete(
        `${API}/admin/database/cleanup-test-users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Cleaned up ${response.data.deleted_operators?.length || 0} test users`);
      fetchData();
    } catch (error) {
      toast.error('Failed to cleanup test users');
    }
  };

  const handleClearCollection = async (collectionName) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${API}/admin/database/collection/${collectionName}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Cleared collection: ${collectionName}`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to clear ${collectionName}`);
    }
  };

  const handleResetDatabase = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.delete(
        `${API}/admin/database/reset-all?confirm=true&keep_admin_users=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Database reset completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to reset database');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/app/admin');
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { variant: 'default', icon: CheckCircle, color: 'text-green-500' },
      trial: { variant: 'secondary', icon: Clock, color: 'text-blue-500' },
      expired: { variant: 'destructive', icon: XCircle, color: 'text-red-500' },
      suspended: { variant: 'destructive', icon: Ban, color: 'text-red-500' },
      canceled: { variant: 'outline', icon: XCircle, color: 'text-gray-500' }
    };
    const config = variants[status] || variants.expired;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`w-3 h-3 ${config.color}`} />
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center gap-2">
          <RefreshCcw className="w-5 h-5 animate-spin" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  // KPI Cards configuration
  const kpiCards = [
    { title: 'Total Operators', value: stats?.total_operators || 0, icon: Users, color: 'text-primary', bg: 'bg-primary/10' },
    { title: 'Active (30 days)', value: stats?.active_operators || 0, icon: TrendingUp, color: 'text-green-500', bg: 'bg-green-500/10' },
    { title: 'Paying Operators', value: stats?.paying_operators || 0, icon: CreditCard, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { title: 'Trial Users', value: stats?.trial_users || 0, icon: Clock, color: 'text-orange-500', bg: 'bg-orange-500/10' },
    { title: 'MRR', value: `€${(stats?.mrr || 0).toFixed(2)}`, icon: DollarSign, color: 'text-green-600', bg: 'bg-green-600/10' },
    { title: 'Churn Rate', value: `${(stats?.churn_rate || 0).toFixed(1)}%`, icon: TrendingUp, color: 'text-red-500', bg: 'bg-red-500/10' },
    { title: 'Payment Failures (7d)', value: stats?.payment_failures || 0, icon: AlertCircle, color: 'text-destructive', bg: 'bg-destructive/10' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-start">
              <img 
                src="/tourpilot-logo.png" 
                alt="TourPilot.biz" 
                className="h-14 max-w-[220px] object-contain"
              />
              <p className="text-xs text-muted-foreground mt-1 whitespace-nowrap">Admin Command Center</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/app/admin/settings')}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCcw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          {kpiCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Card key={index} className="relative overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted-foreground">{card.title}</span>
                    <div className={`p-1.5 rounded-md ${card.bg}`}>
                      <Icon className={`w-4 h-4 ${card.color}`} />
                    </div>
                  </div>
                  <div className="text-2xl font-bold">{card.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Revenue Dashboard */}
        {revenue && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-green-500" />
                  Total Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">€{revenue.total_revenue?.toFixed(2)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-blue-500" />
                  GMV (Packages Value)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">€{revenue.gmv?.toFixed(2)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-purple-500" />
                  ARR
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600">€{revenue.arr?.toFixed(2)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {revenue.monthly_subscriptions} monthly + {revenue.annual_subscriptions} annual
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Alerts Panel */}
        {stats?.expiring_soon && stats.expiring_soon.length > 0 && (
          <Card className="mb-6 border-orange-500/50 bg-orange-500/5">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-orange-600">
                <AlertCircle className="w-5 h-5" />
                Trials Expiring Soon ({stats.expiring_soon.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {stats.expiring_soon.map((op, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-background rounded-lg border">
                    <div>
                      <p className="font-medium text-sm">{op.name}</p>
                      <p className="text-xs text-muted-foreground">{op.email}</p>
                    </div>
                    <Badge variant="outline" className="border-orange-500 text-orange-600">
                      {op.days_remaining} days
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-6 max-w-3xl">
            <TabsTrigger value="operators" className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Operators</span>
            </TabsTrigger>
            <TabsTrigger value="support" className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Support</span>
              {stats?.new_support_requests > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-xs justify-center">
                  {stats.new_support_requests}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-1">
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Payments</span>
            </TabsTrigger>
            <TabsTrigger value="database" className="flex items-center gap-1">
              <Database className="w-4 h-4" />
              <span className="hidden sm:inline">Database</span>
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-1">
              <Activity className="w-4 h-4" />
              <span className="hidden sm:inline">Activity</span>
            </TabsTrigger>
            <TabsTrigger value="revenue" className="flex items-center gap-1">
              <DollarSign className="w-4 h-4" />
              <span className="hidden sm:inline">Revenue</span>
            </TabsTrigger>
          </TabsList>

          {/* Operators Tab */}
          <TabsContent value="operators">
            <Card>
              <CardHeader>
                <CardTitle>All Operators ({operators.length})</CardTitle>
                <CardDescription>Manage operator accounts, subscriptions, and access</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Operator</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Packages</TableHead>
                        <TableHead>Last Login</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {operators.map((op) => (
                        <TableRow key={op.operator_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{op.name}</p>
                              <p className="text-xs text-muted-foreground font-mono">{op.operator_id}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {op.email}
                              {op.email_verified && (
                                <CheckCircle className="w-3 h-3 text-green-500" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(op.subscription_status)}</TableCell>
                          <TableCell>
                            <span className="capitalize">{op.subscription_plan || '-'}</span>
                            {op.days_remaining !== undefined && op.subscription_status === 'trial' && (
                              <p className="text-xs text-muted-foreground">{op.days_remaining} days left</p>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <span className="font-medium">{op.package_count || 0}</span>
                              <span className="text-muted-foreground"> pkgs</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {op.last_login ? new Date(op.last_login).toLocaleDateString() : 'Never'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {!op.email_verified && (
                                <Button size="sm" variant="outline" onClick={() => handleVerifyEmail(op.operator_id)} title="Verify Email">
                                  <Mail className="w-3 h-3" />
                                </Button>
                              )}
                              {op.subscription_status === 'suspended' ? (
                                <Button size="sm" variant="outline" onClick={() => handleReactivate(op.operator_id)}>
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Reactivate
                                </Button>
                              ) : (
                                <Button size="sm" variant="outline" onClick={() => handleSuspend(op.operator_id)}>
                                  <Ban className="w-3 h-3" />
                                </Button>
                              )}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="destructive" title="Delete User">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Operator?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently delete <strong>{op.operator_id}</strong> ({op.email}) and ALL their data including packages, bookings, expenses, and documents.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteOperator(op.operator_id)} className="bg-destructive text-destructive-foreground">
                                      Delete Permanently
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Requests Tab */}
          <TabsContent value="support">
            <Card>
              <CardHeader>
                <CardTitle>Support Requests</CardTitle>
                <CardDescription>View and manage customer support inquiries</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>ID</TableHead>
                        <TableHead>From</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {supportRequests.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            No support requests yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        supportRequests.map((req, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-xs">{req.request_id}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{req.name}</p>
                                <p className="text-xs text-muted-foreground">{req.email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{req.category}</Badge>
                            </TableCell>
                            <TableCell className="max-w-xs">
                              <p className="truncate">{req.subject}</p>
                              <p className="text-xs text-muted-foreground truncate">{req.message}</p>
                            </TableCell>
                            <TableCell>
                              <Badge variant={req.status === 'new' ? 'destructive' : 'default'}>
                                {req.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                              {new Date(req.created_at).toLocaleDateString()}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Failures Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>Payment Failures</CardTitle>
                <CardDescription>Track and resolve failed payment attempts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Session ID</TableHead>
                        <TableHead>Operator</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paymentFailures.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                            No payment failures - all payments successful!
                          </TableCell>
                        </TableRow>
                      ) : (
                        paymentFailures.map((pf, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-xs">{pf.session_id}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{pf.operator_name || pf.operator_id}</p>
                                <p className="text-xs text-muted-foreground">{pf.operator_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>€{pf.amount?.toFixed(2)}</TableCell>
                            <TableCell className="capitalize">{pf.plan}</TableCell>
                            <TableCell className="text-sm">
                              {new Date(pf.created_at).toLocaleDateString()}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Logs Tab */}
          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle>Activity Logs</CardTitle>
                <CardDescription>Track user activities and system events</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>ID</TableHead>
                        <TableHead>Operator</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Timestamp</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activityLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                            No activity logs yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        activityLogs.map((log, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-xs">{log.activity_id}</TableCell>
                            <TableCell className="font-mono text-sm">{log.operator_id}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{log.action}</Badge>
                            </TableCell>
                            <TableCell>{log.description}</TableCell>
                            <TableCell className="text-sm">
                              {new Date(log.timestamp).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue">
            <Card>
              <CardHeader>
                <CardTitle>Revenue Analytics</CardTitle>
                <CardDescription>Monthly revenue breakdown and subscription metrics</CardDescription>
              </CardHeader>
              <CardContent>
                {revenue?.monthly_revenue && revenue.monthly_revenue.length > 0 ? (
                  <div className="space-y-4">
                    <div className="overflow-auto rounded-lg border">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead>Month</TableHead>
                            <TableHead>Revenue</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {revenue.monthly_revenue.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{item.month}</TableCell>
                              <TableCell className="text-green-600 font-medium">€{item.revenue?.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <DollarSign className="w-8 h-8 mx-auto mb-2" />
                    No revenue data yet
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Database Management Tab */}
          <TabsContent value="database">
            <div className="space-y-6">
              {/* Warning Card */}
              <Card className="border-destructive/50 bg-destructive/5">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-destructive mt-0.5" />
                    <div>
                      <p className="font-medium text-destructive">Danger Zone</p>
                      <p className="text-sm text-muted-foreground">
                        These actions are irreversible. Use with extreme caution.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Database Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    Database Statistics
                  </CardTitle>
                  <CardDescription>Current data counts across all collections</CardDescription>
                </CardHeader>
                <CardContent>
                  {dbStats ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {Object.entries(dbStats).map(([collection, count]) => (
                        <div key={collection} className="p-4 border rounded-lg">
                          <p className="text-xs font-medium text-muted-foreground uppercase">{collection}</p>
                          <p className="text-2xl font-bold">{count}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">Loading stats...</p>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                  <CardDescription>Common database management operations</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Cleanup Test Users */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Cleanup Test Users</p>
                      <p className="text-sm text-muted-foreground">
                        Remove all test users (example.com, test_ prefix, etc.) and their data
                      </p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Cleanup
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cleanup Test Users?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete all test users (emails containing example.com, test_, unverified_, verified_) and their associated data.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={handleCleanupTestUsers}>
                            Cleanup Test Users
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>

                  {/* Clear Specific Collections */}
                  <div className="p-4 border rounded-lg space-y-3">
                    <div>
                      <p className="font-medium">Clear Collections</p>
                      <p className="text-sm text-muted-foreground">
                        Clear all documents from specific collections
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {['bookings', 'expenses', 'documents', 'activity_logs', 'deleted_items'].map((coll) => (
                        <AlertDialog key={coll}>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              Clear {coll}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Clear {coll}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete ALL documents from the {coll} collection.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleClearCollection(coll)} className="bg-destructive">
                                Clear Collection
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ))}
                    </div>
                  </div>

                  {/* Full Database Reset */}
                  <div className="flex items-center justify-between p-4 border border-destructive/50 rounded-lg bg-destructive/5">
                    <div>
                      <p className="font-medium text-destructive">Full Database Reset</p>
                      <p className="text-sm text-muted-foreground">
                        Delete ALL data except real operator accounts. This is irreversible!
                      </p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive">
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          Reset Database
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-destructive">⚠️ Full Database Reset</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete ALL packages, bookings, expenses, documents, and test users. Real operator accounts will be preserved but their data will be deleted.
                            <br /><br />
                            <strong>This action cannot be undone!</strong>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={handleResetDatabase} className="bg-destructive">
                            Yes, Reset Everything
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
